import { isValidHex } from '@/lib/colorUtils'
import { Play } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'

export type BoxEntryBannerProps = {
  /** 入口サムネイル（静止画・GIF・MP4）。null ならグラデーションのみの背景になる */
  thumbnailUrl: string | null
  thumbnailMimeType: string | null
  /** バナーに重ねる見出し */
  title: string
  /** ボタン文言 */
  ctaText: string
  /** テナントが設定したブランドカラー。未設定・不正値ならシステム既定色を使う */
  brandColor?: string | null
  /** モーダル表示中はレイアウト寸法を保ったまま背面のバナーだけを隠す */
  isHidden?: boolean
  onClick: () => void
}

/** 種別ラベル。見出しがこれと同じ文言なら二重表示になるので出さない。 */
const EYEBROW_LABEL = 'INTERACTIVE MOVIE'

/**
 * ブランドカラー未設定時に使う moovy ブルー。
 *
 * CVボタンのグラデーション（#4FC3F7 → #2196F3）の明るい側を採る。濃い側の #2196F3 は
 * サムネイル画像の明るい部分に重なるとコントラストが落ちるため、写真の上に置くこの面では
 * 明るい側を使う。
 */
const BRAND_BLUE = '#4FC3F7'

/**
 * BOX 型の入口。
 *
 * 横長カード（480x200）だと埋め込み先のセクション見出しより主張が弱く、バナー広告として
 * 読み飛ばされていた。サムネイルを背景に敷いた大判のシネマティック帯にして、
 * 「動画コンテンツがある」ことを一目で伝える。
 *
 * 文字は左半分に寄せ、そこだけ暗いスクリムを重ねる。全面を暗くすると出演者が沈むため、
 * 右側は素材をそのまま見せる。
 *
 * 動画サムネイルは hover 中だけ再生し、常時通信・常時アニメーションで埋め込み先の
 * 邪魔をしない（横長カード時代からの方針を踏襲）。
 */
const BoxEntryBanner: FC<BoxEntryBannerProps> = ({
  thumbnailUrl,
  thumbnailMimeType,
  title,
  ctaText,
  brandColor,
  isHidden = false,
  onClick,
}) => {
  const cardRef = useRef<HTMLButtonElement>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const isVideo = thumbnailMimeType?.startsWith('video/')
  // 見出しの既定値が 'INTERACTIVE MOVIE'（DEFAULT_BOX_ENTRY_TITLE）なので、顧客が
  // 文言を設定していないと種別ラベルと同じ文字が2つ並ぶ。同じならラベル側を出さない。
  const showEyebrow = title.trim().toUpperCase() !== EYEBROW_LABEL
  const accentColor = typeof brandColor === 'string' && isValidHex(brandColor) ? brandColor : BRAND_BLUE

  useEffect(() => {
    const element = cardRef.current
    if (!element) return

    // 埋め込み枠は幅がまちまちなので、ビューポートではなく自分の幅で切り替える。
    const sync = () => setIsCompact(element.getBoundingClientRect().width < 720)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const preview = previewRef.current
    if (!preview) return

    if (isHovered) {
      void preview.play().catch(() => undefined)
    } else {
      preview.pause()
    }
  }, [isHovered])

  const mediaStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: isHovered ? 'scale(1.04)' : 'scale(1)',
    transition: 'transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  }

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      data-testid="box-entry-banner"
      style={{
        position: 'relative',
        display: 'block',
        visibility: isHidden ? 'hidden' : 'visible',
        width: 'min(100%, 1080px)',
        // 親の幅いっぱいでないときは中央に置く。クレジット（embedded.js が
        // コンテナの兄弟として width:100% + 中央寄せで挿入する）と同じ軸に揃うので、
        // 導入先が CSS で枠を狭めてもバナーとクレジットがズレない。
        marginInline: 'auto',
        height: isCompact ? '380px' : '440px',
        padding: 0,
        overflow: 'hidden',
        border: 0,
        borderRadius: isCompact ? '20px' : '24px',
        background: 'linear-gradient(115deg, #1b2a4a 0%, #34507e 45%, #0f172a 100%)',
        color: '#fff',
        // モーダル側（BoxModal / PCVideoModal）と同一のフォントスタック。
        fontFamily:
          '"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif',
        WebkitFontSmoothing: 'antialiased',
        textAlign: 'left',
        cursor: 'pointer',
        boxShadow: isHovered ? '0 34px 70px -30px rgba(15, 23, 42, 0.6)' : '0 26px 60px -30px rgba(15, 23, 42, 0.5)',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'transform 240ms ease, box-shadow 240ms ease',
        appearance: 'none',
      }}
      aria-hidden={isHidden || undefined}
      tabIndex={isHidden ? -1 : undefined}
      aria-label={`${title}を体験する`}
    >
      {thumbnailUrl &&
        (isVideo ? (
          <video ref={previewRef} src={thumbnailUrl} style={mediaStyle} muted loop playsInline preload="metadata" />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={thumbnailUrl} alt="" style={mediaStyle} />
        ))}

      {/*
       * 文字を置く左側だけ濃く落とすスクリム。全面を均一に暗くすると出演者の顔が沈み、
       * 縦型動画の一番の武器が死ぬため、右側は素材をほぼそのまま残す。
       * 狭い枠では文字が全幅に回り込むので、下方向のグラデーションを足して可読性を保つ。
       */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: isCompact
            ? 'linear-gradient(180deg, rgba(9,15,30,0.35) 0%, rgba(9,15,30,0.82) 100%)'
            : 'linear-gradient(90deg, rgba(9,15,30,0.7) 0%, rgba(9,15,30,0.4) 60%, rgba(9,15,30,0.1) 100%)',
        }}
      />

      <span
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          height: '100%',
          // バナー幅に対する割合で持つ。固定 px だと埋め込み枠を狭めたときに
          // 文字だけが枠いっぱいまで伸びて、写真が見える余地が無くなる。
          maxWidth: isCompact ? '100%' : '70%',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: isCompact ? '0 28px' : '0 56px',
        }}
      >
        {showEyebrow && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: isCompact ? '8px' : '10px',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#fff' }} />
            {EYEBROW_LABEL}
          </span>
        )}

        <span
          style={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            marginTop: showEyebrow ? '8px' : 0,
            marginBottom: isCompact ? '20px' : '26px',
            color: '#fff',
            fontSize: isCompact ? '22px' : '30px',
            fontWeight: 900,
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </span>

        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: isCompact ? '13px 24px' : '16px 32px',
            borderRadius: '999px',
            background: '#fff',
            color: '#0f172a',
            fontSize: isCompact ? '15px' : '16px',
            fontWeight: 700,
            boxShadow: isHovered ? '0 14px 34px rgba(0,0,0,0.38)' : '0 10px 28px rgba(0,0,0,0.3)',
            transition: 'box-shadow 240ms ease',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              width: isCompact ? '30px' : '34px',
              height: isCompact ? '30px' : '34px',
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: accentColor,
              color: '#fff',
            }}
          >
            <Play size={isCompact ? 12 : 14} fill="currentColor" style={{ marginLeft: '2px' }} />
          </span>
          {ctaText}
        </span>
      </span>
    </button>
  )
}

export default BoxEntryBanner
